import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, Loader2, Printer, QrCode } from 'lucide-react'
import apiClient from '@/api/client'
import {
  buildBrandedQr,
  QR_LOGO_SCALE_DEFAULT,
  type BrandedQrShapeStyle,
} from '@/lib/brandedQr'
import { useBrandQrLogo } from '@/hooks/useBrandQrLogo'
import { tableQrUrl } from '@/lib/tableQrUrl'
import { openPosterSheetPrintWindow } from '@/lib/posterExport'
import {
  buildTableTentConfig,
  tableDisplayLabel,
  useTableTentBrand,
  useTableTentDefaults,
} from './tableTentConfig'
import { invalidateTables } from '@/lib/tablesCache'
import { Button } from '@/components/ui/button'
import { DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import type { DiningTable } from '@/types'

const PREVIEW_SIZE = 224
const EXPORT_SIZE = 1024

/**
 * Print-safe shape. Quiet zone 3 and error correction Q match the marketing QR
 * screen, which fixed both deliberately: a table code is read off a laminated
 * card in restaurant lighting, not off a monitor. A centre logo overrides the
 * level to H inside buildBrandedQrOptions — that rule lives there, not here.
 */
const TABLE_QR_STYLE: BrandedQrShapeStyle = {
  dark_color: '#000000',
  light_color: '#FFFFFF',
  margin_modules: 3,
  dots_type: 'square',
  corner_square_type: 'square',
  corner_dot_type: 'square',
  error_correction: 'Q',
}

/**
 * The QR for one table (F-FE-N100).
 *
 * Regenerate is guarded by an explicit confirm rather than an undo: the thing
 * it invalidates is a laminated card on a table, and there is no way to take
 * that back from the dining room.
 */
export function TableQrSheet({ table, onClose }: { table: DiningTable; onClose: () => void }) {
  const [printing, setPrinting] = useState(false)
  const tentDefaults = useTableTentDefaults()
  const brand = useTableTentBrand()

  const qc = useQueryClient()
  const host = useRef<HTMLDivElement>(null)
  const [confirming, setConfirming] = useState(false)

  // Shares the ['settings','all'] cache with the rest of the admin app, so
  // this costs no extra request.
  const { data: settings } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
    staleTime: 1000 * 60 * 5,
  })
  // Orders are accepted on EITHER flag, so the warning has to require both to
  // be off. Keying it on the dine-in flag alone told every tenant with the full
  // online-ordering add-on that their codes would not work, while they did.
  const capabilityOff = (() => {
    const all = settings?.data as Record<string, unknown> | undefined
    if (!all) return false
    return all.feature_dine_in_qr === false && all.feature_online_ordering === false
  })()

  // The origin the printed code points at. Read from the resolver, NOT from a
  // settings row: GET /settings only ever emits what is stored in app_settings,
  // and the public base usually comes from MARKETING_QR_REDIRECT_BASE or from
  // auto-detect. Reading a `public_base_url` key off /settings would be empty
  // on almost every tenant and this sheet would print nothing.
  const publicLinks = useQuery({
    queryKey: ['public-links'],
    queryFn: () => apiClient.getPublicLinks(),
    staleTime: 60_000,
  })
  const publicBase = publicLinks.data?.data?.resolved?.url ?? ''
  // False when the resolved host is localhost or a private LAN address — such a
  // code prints fine and cannot be opened from a phone on cellular data.
  const scannable = publicLinks.data?.data?.resolved?.is_scannable !== false
  // The storefront origin the /q/t/<slug> redirect hands the diner to. Distinct
  // from publicBase above, which is the POS host that merely serves /q/. Unset
  // means a scanned code lands nowhere, so it must block printing.
  const orderingBase = publicLinks.data?.data?.ordering?.url ?? ''

  const ensure = useQuery({
    queryKey: ['table-qr', table.id],
    queryFn: () => apiClient.ensureTableQr(table.id),
  })
  const slug = ensure.data?.data?.qr_slug ?? ''
  const url = tableQrUrl(publicBase, slug)

  const { logoDataUrl, brandedQrEnabled } = useBrandQrLogo()

  useEffect(() => {
    const node = host.current
    if (!node) return
    node.innerHTML = ''
    if (!url) return
    buildBrandedQr({
      data: url,
      size: PREVIEW_SIZE,
      style: TABLE_QR_STYLE,
      logoDataUrl,
      logoEnabled: brandedQrEnabled,
      logoScale: QR_LOGO_SCALE_DEFAULT,
    }).append(node)
  }, [url, logoDataUrl, brandedQrEnabled])

  /**
   * Reprint the tent for this one table.
   *
   * Goes through the same A5 tent builder and the same print path as "Print
   * table QRs", so a replacement card for table 7 is indistinguishable from the
   * thirty-nine printed in the bulk run — a reprint that looked different would
   * read as a repair on the floor.
   *
   * The sheet printer takes a list and reverts the page break on the last
   * poster, so a list of one is exactly one page with no trailing blank.
   */
  const handlePrint = async () => {
    if (!url) return
    setPrinting(true)
    try {
      await openPosterSheetPrintWindow({
        posters: [{ config: buildTableTentConfig(tentDefaults, table.table_number, url) }],
        brand,
        purpose: 'dine_in',
        documentTitle: `${tableDisplayLabel(table.table_number)} QR tent`,
      })
    } catch (error) {
      toastHelpers.apiError('Print table tent', error)
    } finally {
      setPrinting(false)
    }
  }

  // Same export path as the Marketing QR tab: render at print resolution and
  // hand back the raw PNG blob, rather than screenshotting the preview.
  const downloadPng = async () => {
    if (!url) return
    try {
      const qr = buildBrandedQr({
        data: url,
        size: EXPORT_SIZE,
        style: TABLE_QR_STYLE,
        logoDataUrl,
        logoEnabled: brandedQrEnabled,
        logoScale: QR_LOGO_SCALE_DEFAULT,
      })
      const blob = await qr.getRawData('png')
      if (!blob || !(blob instanceof Blob)) throw new Error('no blob')
      const safe =
        String(table.table_number).replace(/[^a-zA-Z0-9-_]+/g, '_').slice(0, 40) || 'table'
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `table-${safe}-qr.png`
      a.click()
      URL.revokeObjectURL(href)
    } catch {
      toastHelpers.error('Export failed', 'Could not generate the PNG.')
    }
  }

  const rotate = useMutation({
    mutationFn: () => apiClient.rotateTableQr(table.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['table-qr', table.id] })
      // The table lists carry qr_slug too; leaving them stale would tell the
      // next screen that a code exists which no longer does.
      invalidateTables(qc)
      setConfirming(false)
      toastHelpers.success(
        'New code generated',
        `Reprint the card for table ${table.table_number}.`,
      )
    },
    onError: (error: unknown) => toastHelpers.apiError('Regenerate table QR', error),
  })

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5" /> {tableDisplayLabel(table.table_number)} QR
        </DialogTitle>
        <DialogDescription>
          Print this and place it on the table. Guests scan it to order straight to this table
          (dine-in).
        </DialogDescription>
      </DialogHeader>

      {capabilityOff && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-950/30">
          These codes will not accept orders until dine-in QR ordering is enabled for this
          restaurant. You can still print them.
        </p>
      )}

      {!orderingBase && !publicLinks.isLoading && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          No customer ordering address is configured, so scanning this code lands on the staff
          login instead of your menu. Set the storefront URL before printing.
        </p>
      )}

      {!publicBase && !publicLinks.isLoading && (
        <p className="text-sm text-destructive">
          No public address is configured, so a printed code could not be scanned. Set it in
          Settings before printing.
        </p>
      )}

      {publicBase && !scannable && (
        <p className="text-sm text-destructive">
          The public address for this restaurant ({publicBase}) is a local one, so a printed code
          will not open from a customer phone. Fix it in Settings before printing.
        </p>
      )}

      {ensure.isError && (
        <p className="text-sm text-destructive">
          Could not generate a code for this table. Try again, or reload the page.
        </p>
      )}

      <div className="flex min-h-[240px] items-center justify-center">
        {url ? (
          <div ref={host} className="rounded-lg border border-border p-2" />
        ) : (
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        )}
      </div>

      {url && (
        <p className="break-all text-center font-mono text-xs text-muted-foreground">{url}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
        <Button onClick={handlePrint} disabled={!url || printing} className="gap-2">
          {printing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          Print tent
        </Button>
        <Button onClick={downloadPng} disabled={!url} variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Download PNG
        </Button>
        {confirming ? (
          <>
            <Button
              variant="destructive"
              disabled={rotate.isPending}
              onClick={() => rotate.mutate()}
            >
              {rotate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes, replace this code
            </Button>
            <Button variant="ghost" onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <p className="w-full text-sm text-muted-foreground">
              The printed code on this table stops working immediately. You will need to reprint it.
            </p>
          </>
        ) : (
          <Button variant="outline" disabled={!slug} onClick={() => setConfirming(true)}>
            Regenerate
          </Button>
        )}
      </div>
    </div>
  )
}
